import { useInfiniteQuery } from '@tanstack/react-query';
import { Bell, CircleAlert, X } from 'lucide-react';
import { useSearchParams } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge, type Tone } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import {
  eventDetail,
  eventsKey,
  kindLabels,
  listEvents,
  severityLabels,
  type EventFilter,
  type Severity,
} from '../features/events/api';
import { useLiveStatus, type LiveStatus } from '../features/events/LiveEvents';
import { useTimeFormat } from '../lib/useTimeFormat';

const severityTone: Record<Severity, Tone> = { critical: 'crit', warning: 'warn', info: 'neutral' };

const liveBadge: Record<LiveStatus, { tone: Tone; label: string; pulse?: boolean }> = {
  live: { tone: 'ok', label: 'Em tempo real', pulse: true },
  connecting: { tone: 'neutral', label: 'Conectando…' },
  reconnecting: { tone: 'warn', label: 'Reconectando…' },
  off: { tone: 'neutral', label: 'Tempo real indisponível' },
};

function readFilter(p: URLSearchParams): EventFilter {
  const sev = p.get('severity');
  return {
    severity: sev === 'critical' || sev === 'warning' || sev === 'info' ? sev : '',
    kind: p.get('kind') ?? '',
    device: p.get('device') ?? '',
  };
}

const selectClass =
  'h-8 rounded-md border border-line-strong bg-canvas px-2 text-[13px] text-fg hover:border-fg-subtle/60 focus:border-accent';

/**
 * Registros: what happened on the devices, as a log (scope review of
 * 08/10/2026: no monitoring console, nothing to acknowledge).
 */
export function EventsPage() {
  const { request } = useAuth();
  const fmt = useTimeFormat();
  const live = useLiveStatus();
  const [params, setParams] = useSearchParams();
  const filter = readFilter(params);

  const update = (patch: Partial<Record<'severity' | 'kind' | 'device', string>>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  };

  const list = useInfiniteQuery({
    queryKey: [...eventsKey, 'list', filter],
    queryFn: ({ pageParam }) => listEvents(request, filter, pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_before ?? undefined,
  });

  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(filter.severity || filter.kind || filter.device);
  const deviceName = filter.device
    ? (items.find((e) => e.device?.id === filter.device)?.device?.name ?? 'selecionado')
    : '';
  const badge = liveBadge[live];

  return (
    <>
      <PageHeader
        title="Registros"
        description={`O que acontece nos equipamentos, do mais recente ao mais antigo. Horários em ${fmt.timeZone ?? 'fuso local'}.`}
        actions={
          <StatusBadge tone={badge.tone} pulse={badge.pulse ?? false}>
            {badge.label}
          </StatusBadge>
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        <div role="search" className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Filtrar por severidade"
            value={filter.severity}
            onChange={(e) => {
              update({ severity: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todas as severidades</option>
            {(['critical', 'warning', 'info'] as const).map((s) => (
              <option key={s} value={s}>
                {severityLabels[s]}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por tipo"
            value={filter.kind}
            onChange={(e) => {
              update({ kind: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os tipos</option>
            {Object.entries(kindLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          {filter.device && (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong bg-surface-raised pr-1 pl-2.5 text-[13px] text-fg">
              Dispositivo: {deviceName || 'excluído'}
              <button
                type="button"
                aria-label="Remover filtro de dispositivo"
                onClick={() => {
                  update({ device: '' });
                }}
                className="grid size-6 place-items-center rounded text-fg-subtle hover:bg-surface-hover hover:text-fg"
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            </span>
          )}
          {filtered && (
            <Button
              variant="ghost"
              onClick={() => {
                update({ severity: '', kind: '', device: '' });
              }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {list.isError && !list.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os registros"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void list.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : list.data && items.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="Nenhum registro"
              description={
                filtered
                  ? 'Nenhum registro corresponde aos filtros aplicados.'
                  : 'Quedas, retornos, movimento e outras ocorrências dos equipamentos aparecem aqui assim que acontecem.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="block w-full text-left text-[13px] sm:table sm:min-w-[820px]"
                aria-busy={list.isFetching}
              >
                <caption className="sr-only">Registros, do mais recente ao mais antigo</caption>
                <thead className="hidden text-2xs tracking-wide text-fg-subtle uppercase sm:table-header-group">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Severidade
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Quando
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Ocorrência
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Dispositivo
                    </th>
                  </tr>
                </thead>
                <tbody className="block sm:table-row-group">
                  {!list.data
                    ? Array.from({ length: 8 }, (_, i) => <SkeletonRow key={i} />)
                    : items.map((e) => {
                        const label = kindLabels[e.kind] ?? e.kind;
                        const detail = eventDetail(e, fmt.absolute);
                        return (
                          <tr
                            key={e.id}
                            className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 border-b border-line px-4 py-3 last:border-0 hover:bg-surface-hover/50 sm:table-row sm:p-0"
                          >
                            <td className="sm:px-4 sm:py-2.5">
                              <StatusBadge tone={severityTone[e.severity]}>
                                {severityLabels[e.severity]}
                              </StatusBadge>
                            </td>
                            <td className="text-right font-mono text-xs whitespace-nowrap text-fg-muted tabular-nums sm:px-4 sm:py-2.5 sm:text-left">
                              <time dateTime={e.occurred_at}>{fmt.precise(e.occurred_at)}</time>
                            </td>
                            <td className="col-span-2 sm:px-4 sm:py-2.5">
                              <div className="text-fg">{label}</div>
                              {detail && <div className="text-xs text-fg-subtle">{detail}</div>}
                            </td>
                            <td className="col-span-2 text-xs sm:px-4 sm:py-2.5 sm:text-[13px]">
                              {e.device ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    update({ device: e.device?.id ?? '' });
                                  }}
                                  title="Ver os registros deste dispositivo"
                                  className="max-w-56 truncate text-left text-fg hover:text-accent hover:underline"
                                >
                                  {e.device.name || 'Dispositivo excluído'}
                                </button>
                              ) : (
                                <span className="text-fg-subtle">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}
          {list.data && items.length > 0 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-xs text-fg-muted">
              <span aria-live="polite">{items.length} registros carregados</span>
              {list.hasNextPage ? (
                <Button
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                  className="h-7"
                >
                  {list.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                </Button>
              ) : (
                <span>Fim do registro para este filtro</span>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function SkeletonRow() {
  return (
    <tr aria-hidden="true" className="block border-b border-line last:border-0 sm:table-row">
      {['w-16', 'w-32', 'w-36', 'w-28'].map((w, i) => (
        <td key={i} className="block px-4 py-2 sm:table-cell sm:py-3.5">
          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
        </td>
      ))}
    </tr>
  );
}
